import { DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import {
  computeProcessingElapsedMs,
  formatRuntimeHhMmSs,
  type PageSyncState,
  type ProcessingTimerSyncState,
} from '@ecam/eventq-components';
import { isEventsLandingRoutePath, isLoadingRoutePath } from '../routing/browser-tab-routes';
import type {
  ProcessingTimerCompleteReason,
  ProcessingTimerEntityType,
} from '../models/entities/processing-timer.model';
import { resolveProcessingClaimedAt } from '../utils/processing-claimed-at.util';
import { AgentEventsService } from './agent-events.service';
import { BrowserTabService } from './browser-tab.service';

interface LocalProcessingTimer extends ProcessingTimerSyncState {
  tabId: string;
}

/** Per-tab HH:MM:SS processing label. Local only; TIMER_SYNC / TIMER_CLEAR stay on the bus. */
@Injectable({ providedIn: 'root' })
export class ProcessingTimerService {
  private static readonly REMOTE_TIMER_SKEW_MS = 500;

  private readonly browserTabs = inject(BrowserTabService);
  private readonly agentEvents = inject(AgentEventsService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly timers = signal<Record<string, LocalProcessingTimer>>({});
  readonly nowMs = signal(Date.now());
  private tickId: number | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.stopTick());

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.nowMs.set(Date.now());
      });

    fromEvent(window, 'focus')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.nowMs.set(Date.now()));

    fromEvent(window, 'pagehide')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.completeAll('page-shutdown');
      });

    this.agentEvents.events$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event.type === 'TIMER_SYNC' && event.processingTimer) {
        this.applyRemote(event.processingTimer);
        return;
      }
      if (event.type === 'TIMER_CLEAR' && event.processingTimer) {
        this.clearLocal(event.processingTimer.correlationId);
        return;
      }
      if (event.type === 'STATE_SYNC' && event.pageState) {
        this.syncTimersFromPageState(event.pageState);
      }
    });

    effect(() => {
      this.browserTabs.tabDetails();
      untracked(() => this.hydrateOpenProcessingTabs());
    });
  }

  syncStateForEntity(entityId: string): ProcessingTimerSyncState | null {
    const match = Object.values(this.timers()).find((item) => item.entityId === entityId);
    return match ? this.toSyncState(match) : null;
  }

  syncTimersFromPageState(pageState: PageSyncState): void {
    for (const event of pageState.events ?? []) {
      if (event.processingTimer?.running) {
        this.applyRemote(event.processingTimer);
      }
    }
  }

  runtimeLabelForTab(tabId: string): string | null {
    this.nowMs();
    const pageState = this.browserTabs.getPageState(tabId);
    if (isEventsLandingRoutePath(pageState.routePath) || isLoadingRoutePath(pageState.routePath)) {
      return null;
    }

    const timer = this.timerForTab(tabId, pageState.activeEventId);
    if (!timer?.running) {
      return null;
    }

    const elapsed = computeProcessingElapsedMs(
      timer.prevElapsedMs,
      timer.segmentStartAt,
      this.nowMs(),
    );
    return formatRuntimeHhMmSs(elapsed);
  }

  startForTab(
    tabId: string,
    input: { entityId: string; entityType: ProcessingTimerEntityType; claimedAt?: string },
  ): void {
    const tab = this.browserTabs.getTab(tabId);
    const correlationId = tab?.instanceId;
    if (!correlationId) {
      return;
    }

    const incoming =
      input.claimedAt?.trim() || this.browserTabs.getPageState(tabId).claimedAt?.trim() || '';
    const claimedAt = resolveProcessingClaimedAt(incoming || undefined);

    const existing = this.timerForTab(tabId, input.entityId);
    if (existing?.running && existing.entityId === input.entityId) {
      if (existing.segmentStartAt === claimedAt || !incoming) {
        return;
      }
    }

    this.browserTabs.updatePageState(tabId, { claimedAt });

    const next: LocalProcessingTimer = {
      tabId,
      correlationId,
      entityId: input.entityId,
      entityType: input.entityType,
      prevElapsedMs: 0,
      segmentStartAt: claimedAt,
      running: true,
    };
    this.setTimer(next);
    this.agentEvents.broadcastTimerSync?.(this.toSyncState(next));
  }

  completeForTab(tabId: string, _reason: ProcessingTimerCompleteReason): void {
    const timer = this.timerForTab(tabId, this.browserTabs.getPageState(tabId).activeEventId);
    if (!timer) {
      return;
    }
    this.completeTimer(timer);
  }

  completeAll(_reason: ProcessingTimerCompleteReason): void {
    const snapshot = Object.values(this.timers());
    for (const timer of snapshot) {
      this.completeTimer(timer);
    }
  }

  hydrateOpenProcessingTabs(): void {
    for (const tab of this.browserTabs.tabs()) {
      const state = this.browserTabs.getPageState(tab.id);
      const entityId = state.activeEventId;
      const mode = state.eventProcessingMode;
      if (
        !entityId ||
        isEventsLandingRoutePath(state.routePath) ||
        isLoadingRoutePath(state.routePath)
      ) {
        continue;
      }
      this.startForTab(tab.id, {
        entityId,
        entityType: mode,
        claimedAt: state.claimedAt ?? undefined,
      });
    }
  }

  applyRemote(remote: ProcessingTimerSyncState): void {
    if (!remote.running) {
      this.clearLocal(remote.correlationId);
      return;
    }

    const tabId = this.resolveTabIdForRemote(remote);
    if (!tabId) {
      return;
    }

    const local = this.timerForTab(tabId, remote.entityId);
    const now = this.nowMs();
    if (local?.running) {
      const localElapsed = this.elapsedMsFor(local, now);
      const remoteElapsed = this.elapsedMsFor(remote, now);
      if (remoteElapsed + ProcessingTimerService.REMOTE_TIMER_SKEW_MS < localElapsed) {
        return;
      }
    }

    this.setTimer({
      ...remote,
      tabId,
    });
  }

  private elapsedMsFor(state: ProcessingTimerSyncState, atMs: number): number {
    return computeProcessingElapsedMs(state.prevElapsedMs, state.segmentStartAt, atMs);
  }

  private timerForTab(
    tabId: string,
    entityId: string | null | undefined,
  ): LocalProcessingTimer | undefined {
    const tab = this.browserTabs.getTab(tabId);
    const correlationId = tab?.instanceId;
    return (
      (correlationId ? this.timers()[correlationId] : undefined) ??
      (entityId
        ? Object.values(this.timers()).find(
            (item) => item.running && item.entityId === entityId && item.tabId === tabId,
          )
        : undefined)
    );
  }

  private resolveTabIdForRemote(remote: ProcessingTimerSyncState): string {
    const byCorrelation =
      this.browserTabs.tabs().find((tab) => tab.instanceId === remote.correlationId)?.id ?? '';
    if (byCorrelation) {
      return byCorrelation;
    }

    const byEntity =
      this.browserTabs
        .tabs()
        .find((tab) => this.browserTabs.getPageState(tab.id).activeEventId === remote.entityId)
        ?.id ?? '';
    if (byEntity) {
      return byEntity;
    }

    return this.timers()[remote.correlationId]?.tabId ?? '';
  }

  private completeTimer(timer: LocalProcessingTimer): void {
    this.clearLocal(timer.correlationId);
    this.agentEvents.broadcastTimerClear?.(this.toSyncState({ ...timer, running: false }));
  }

  private setTimer(timer: LocalProcessingTimer): void {
    this.timers.update((current) => {
      const next = { ...current };
      for (const [key, existing] of Object.entries(current)) {
        if (existing.tabId === timer.tabId && key !== timer.correlationId) {
          delete next[key];
        }
      }
      next[timer.correlationId] = timer;
      return next;
    });
    this.ensureTick();
  }

  private clearLocal(correlationId: string): void {
    this.timers.update((current) => {
      const next = { ...current };
      delete next[correlationId];
      return next;
    });
    if (Object.keys(this.timers()).length === 0) {
      this.stopTick();
    }
  }

  private ensureTick(): void {
    if (this.tickId != null) {
      return;
    }
    this.tickId = window.setInterval(() => this.nowMs.set(Date.now()), 1000) as unknown as number;
  }

  private stopTick(): void {
    if (this.tickId != null) {
      window.clearInterval(this.tickId);
      this.tickId = null;
    }
  }

  private toSyncState(timer: LocalProcessingTimer): ProcessingTimerSyncState {
    return {
      correlationId: timer.correlationId,
      entityId: timer.entityId,
      entityType: timer.entityType,
      prevElapsedMs: timer.prevElapsedMs,
      segmentStartAt: timer.segmentStartAt,
      running: timer.running,
    };
  }
}
